import React, { useState, useEffect, useRef } from "react";
import { useQuote } from "../context/QuoteContext";
import api, { API } from "../api";
import { formatDateIt, parseDurationDiscounts, parseGaugePeriods } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { X, Plus, Trash2, Save, Loader2, FileSpreadsheet, Upload, Download, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import AiUsageStats from "./AiUsageStats";
import UserManagement from "./UserManagement";
import ActivityLog from "./ActivityLog";

const CATS = ["mobili", "elettrodomestici", "scatole", "altro"];

const SECTIONS = [
  ["prezzi", "Prezzi e tariffe"],
  ["sconti-durata", "Sconti durata"],
  ["testi", "Testi messaggio"],
  ["profitometro", "Profit-o-metro"],
  ["prompt-ai", "Prompt AI"],
  ["modelli-ai", "Modelli AI"],
  ["statistiche", "Statistiche AI"],
  ["self-storage", "Self-storage"],
  ["catalogo", "Catalogo"],
  ["materiali-imballaggio", "Materiali imballaggio"],
  ["sconti-volume", "Sconti volume"],
];

// setting key -> section (mirror of backend SETTING_SECTIONS)
const KEY_SECTION = {
  pricePerM3: "prezzi", tariffaOraria: "prezzi", numPersoneDefault: "prezzi", riempimentoDefault: "prezzi",
  fattoreStradaKm: "prezzi", fattoreTrafficoMin: "prezzi", msgAbitiPrice: "prezzi",
  durationDiscounts: "sconti-durata",
  depotAddress: "testi", msgGreeting: "testi", msgIntro: "testi", msgIntroArea: "testi", msgAreaDisclaimer: "testi",
  msgDurationNote: "testi", msgAlternativa: "testi", msgAbitiDesc: "testi", msgAbitiClose: "testi",
  msgConsegnaMateriali: "testi", msgWhy: "testi",
  gaugePeriods: "profitometro", gaugeRedMax: "profitometro", gaugeYellowMax: "profitometro", gaugeMax: "profitometro",
  msgPolishPrompt: "prompt-ai",
  textAiEnabled: "prompt-ai", textAiPrompt: "prompt-ai", simpleChatPrompt: "prompt-ai",
  modelPolish: "modelli-ai", modelExtract: "modelli-ai", modelPhoto: "modelli-ai", modelCap: "modelli-ai",
  modelTravel: "modelli-ai", modelMarket: "modelli-ai", modelBoxRental: "modelli-ai", modelSelfStorage: "modelli-ai", modelPrices: "modelli-ai", modelSimpleChat: "modelli-ai",
  ssCanone: "self-storage", ssCauzione: "self-storage", ssTrasporto: "self-storage",
  catalogFileName: "catalogo", catalogFileUpdatedAt: "catalogo", catalogFilePath: "catalogo",
};


const AI_MODELS = [
  "gpt-4o", "gpt-4o-mini", "gpt-4.1", "gpt-4.1-mini",
  "gpt-5", "gpt-5-mini", "gpt-5.6-sol", "gpt-5.6-terra", "gpt-5.6-luna",
];

// Stime indicative (prezzo input/output per 1M token, USD) — verifica sempre il listino OpenAI
const MODEL_META = {
  "gpt-4o":        { tier: "Bilanciato",    speed: "Veloce",       price: "$2,50 / $10 per 1M" },
  "gpt-4o-mini":   { tier: "Economico",     speed: "Molto veloce", price: "$0,15 / $0,60 per 1M" },
  "gpt-4.1":       { tier: "Bilanciato",    speed: "Veloce",       price: "$2 / $8 per 1M" },
  "gpt-4.1-mini":  { tier: "Economico",     speed: "Molto veloce", price: "$0,40 / $1,60 per 1M" },
  "gpt-5":         { tier: "Potente",       speed: "Medio",        price: "~$1,25 / $10 per 1M" },
  "gpt-5-mini":    { tier: "Economico",     speed: "Veloce",       price: "~$0,25 / $2 per 1M" },
  "gpt-5.6-sol":   { tier: "Molto potente", speed: "Lento",        price: "$5 / $30 per 1M" },
  "gpt-5.6-terra": { tier: "Potente",       speed: "Medio",        price: "$2 / $12 per 1M" },
  "gpt-5.6-luna":  { tier: "Economico",     speed: "Veloce",       price: "$0,20 / $1,20 per 1M" },
};

const TIER_COLOR = {
  "Economico": "bg-emerald-100 text-emerald-700",
  "Bilanciato": "bg-sky-100 text-sky-700",
  "Potente": "bg-amber-100 text-amber-700",
  "Molto potente": "bg-red-100 text-red-700",
};

const AI_FUNCTIONS = [
  ["modelPolish", "Ottimizza con l'AI (Polish)"],
  ["modelExtract", "Estrazione oggetti da testo"],
  ["modelPhoto", "Analisi foto (visione)"],
  ["modelCap", "Ricerca CAP / coordinate"],
  ["modelTravel", "Stima distanza e tempo"],
  ["modelMarket", "Ricerca mercato self-storage (Oggetti)"],
  ["modelBoxRental", "Mercato box in affitto (Area)"],
  ["modelSelfStorage", "Mercato self-storage (Area)"],
  ["modelPrices", "Stima prezzi oggetti"],
  ["modelSimpleChat", "Chat semplificata (Da testo semplificato)"],
];

export default function SettingsPanel({ onClose, onOpenGuide }) {
  const { settings, catalog, loadSettings, loadCatalog, loadDiscounts, user, packingCatalog, loadPackingCatalog } = useQuote();
  const isAdmin = user?.role === "admin";
  const allowedSet = isAdmin ? null : new Set(user?.allowed_sections || ["testi"]);
  const vis = (id) => isAdmin || allowedSet.has(id);
  const navSections = isAdmin ? [...SECTIONS, ["gestione-utenti", "Gestione utenti"], ["registro-attivita", "Registro attività"]] : SECTIONS.filter(([id]) => vis(id));
  const [local, setLocal] = useState(settings || {});
  const [rows, setRows] = useState(catalog.map((c) => ({ ...c })));
  const [packRows, setPackRows] = useState((packingCatalog || []).map((c) => ({ ...c })));
  const [durDiscs, setDurDiscs] = useState(parseDurationDiscounts(settings?.durationDiscounts));
  const [gaugePeriods, setGaugePeriods] = useState(parseGaugePeriods(settings?.gaugePeriods));
  const [saving, setSaving] = useState(false);
  const [activeSec, setActiveSec] = useState(navSections[0]?.[0] || "prezzi");
  const secShow = (id) => vis(id) && activeSec === id;
  const panelRef = useRef();

  useEffect(() => {
    const root = panelRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSec(e.target.id.replace("sec-", "")); });
      },
      { root, rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    SECTIONS.forEach(([id]) => { const el = document.getElementById(`sec-${id}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);


  const [discLabel, setDiscLabel] = useState(catalog[0]?.label || "");
  const [tiers, setTiers] = useState([]);
  const [discSaving, setDiscSaving] = useState(false);

  const [fileInfo, setFileInfo] = useState({ fileName: "", updatedAt: "" });
  const [reuploading, setReuploading] = useState(false);
  const xlsxRef = useRef();

  useEffect(() => { api.get("/catalog/file-info").then(({ data }) => setFileInfo(data)).catch(() => {}); }, []);

  const reupload = async (file) => {
    if (!file) return;
    setReuploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/catalog/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadCatalog();
      const { data: fi } = await api.get("/catalog/file-info");
      setFileInfo(fi);
      toast.success(`Listino aggiornato: ${data.count} articoli${data.estimated ? ` (${data.estimated} stimati AI)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'importazione del listino");
    } finally {
      setReuploading(false);
    }
  };

  useEffect(() => { setLocal(settings || {}); setDurDiscs(parseDurationDiscounts(settings?.durationDiscounts)); setGaugePeriods(parseGaugePeriods(settings?.gaugePeriods)); }, [settings]);
  useEffect(() => { setRows(catalog.map((c) => ({ ...c }))); if (!discLabel && catalog[0]) setDiscLabel(catalog[0].label); }, [catalog]);
  useEffect(() => { setPackRows((packingCatalog || []).map((c) => ({ ...c }))); }, [packingCatalog]);
  const setPackRow = (i, k, v) => setPackRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addPackRow = () => setPackRows((rs) => [...rs, { label: "Nuovo materiale", dimensions: "", stocked: "", cost_eur: 0, client_eur: 0, sort_order: rs.length }]);
  const delPackRow = (i) => setPackRows((rs) => rs.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (!discLabel) return;
    api.get(`/volume-discounts/${encodeURIComponent(discLabel)}`).then(({ data }) =>
      setTiers(data.discounts.map((d) => ({ min_qty: d.min_qty, max_qty: d.max_qty ?? "", price_per_unit: d.price_per_unit })))
    );
  }, [discLabel]);

  const setS = (k, v) => setLocal((l) => ({ ...l, [k]: v }));
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows((rs) => [...rs, { label: "Nuovo oggetto", category: "altro", price_eur: 0, length_cm: 0, width_cm: 0, height_cm: 0, fach_minutes: 0, imb_minutes: 0, count_in_profit: true, sort_order: rs.length }]);
  const delRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const canEdit = (s) => isAdmin || (allowedSet && allowedSet.has(s));

  const save = async () => {
    setSaving(true);
    try {
      const cleanDiscs = durDiscs
        .map((d) => ({ months: Number(d.months) || 0, percent: Number(d.percent) || 0 }))
        .filter((d) => d.months > 0 && d.percent > 0);
      const cleanGauge = gaugePeriods
        .map((p) => ({ months: Number(p.months) || 0, redMax: Number(p.redMax) || 0, yellowMax: Number(p.yellowMax) || 0, max: Number(p.max) || 0 }))
        .filter((p) => p.months > 0 && p.max > 0);
      // send only the settings keys the user is allowed to edit (server enforces this too)
      const full = { ...local, durationDiscounts: JSON.stringify(cleanDiscs), gaugePeriods: JSON.stringify(cleanGauge) };
      const payload = {};
      Object.keys(full).forEach((k) => {
        const sec = KEY_SECTION[k];
        if (sec && canEdit(sec)) payload[k] = full[k];
      });
      if (Object.keys(payload).length) await api.post("/settings", { settings: payload });
      if (canEdit("catalogo")) await api.post("/catalog", { items: rows.map((r, i) => ({ ...r, sort_order: i })) });
      if (canEdit("materiali-imballaggio")) await api.post("/packing-materials", { items: packRows.map((r, i) => ({ ...r, sort_order: i })) });
      await loadSettings(); await loadCatalog(); await loadPackingCatalog();
      toast.success("Impostazioni salvate");
      onClose();
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveDiscounts = async () => {
    setDiscSaving(true);
    try {
      await api.post(`/volume-discounts/${encodeURIComponent(discLabel)}`, {
        tiers: tiers.map((t) => ({ min_qty: Number(t.min_qty) || 1, max_qty: t.max_qty === "" ? null : Number(t.max_qty), price_per_unit: Number(t.price_per_unit) || 0 })),
      });
      await loadDiscounts();
      toast.success("Sconti volume salvati");
    } catch {
      toast.error("Errore salvataggio sconti");
    } finally {
      setDiscSaving(false);
    }
  };

  const num = (k) => (
    <Input type="number" value={local[k] ?? ""} onChange={(e) => setS(k, parseFloat(e.target.value) || 0)} className="mt-1" data-testid={`setting-${k}`} />
  );

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col slide-up" data-testid="settings-panel">
      <div className="bg-[#1a5f5a] text-white shrink-0">
        <div className="max-w-[1250px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-display font-semibold">Impostazioni</h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onOpenGuide} className="text-white hover:bg-white/15 hover:text-white gap-1.5" data-testid="settings-guide-button">
              <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">Guida</span>
            </Button>
            <Button onClick={save} disabled={saving} className="bg-white text-[#1a5f5a] hover:bg-white/90 gap-2" data-testid="settings-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva impostazioni
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/15" data-testid="settings-close-button"><X className="h-5 w-5" /></Button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full max-w-[1250px] mx-auto flex">
        <nav className="w-44 sm:w-56 shrink-0 border-r border-slate-200 overflow-y-auto py-4 px-2 bg-slate-50/60" data-testid="settings-nav">
          {navSections.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setActiveSec(id)} data-testid={`settings-nav-${id}`}
              className={`w-full text-left text-sm px-3 py-2 rounded-md mb-0.5 transition-colors ${activeSec === id ? "bg-[#1a5f5a] text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </nav>

        <div ref={panelRef} className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-6 py-8 space-y-8">
        <section id="sec-prezzi" className={`${secShow("prezzi") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-4">Prezzi e tariffe</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><Label className="text-xs">Tariffa per m³ (€)</Label>{num("pricePerM3")}</div>
            <div><Label className="text-xs">Tariffa oraria manodopera (€)</Label>{num("tariffaOraria")}</div>
            <div><Label className="text-xs">Numero di persone default</Label>{num("numPersoneDefault")}</div>
            <div><Label className="text-xs">Riempimento default area (%)</Label>{num("riempimentoDefault")}</div>
            <div><Label className="text-xs">Fattore stradale (×)</Label>{num("fattoreStradaKm")}</div>
            <div><Label className="text-xs">Fattore traffico (×)</Label>{num("fattoreTrafficoMin")}</div>
            <div><Label className="text-xs">Prezzo scatola porta abiti (€/mese)</Label><Input type="number" value={local.msgAbitiPrice ?? ""} onChange={(e) => setS("msgAbitiPrice", parseFloat(e.target.value) || 0)} className="mt-1" data-testid="setting-msgAbitiPrice" /></div>
          </div>
        </section>

        <section id="sec-sconti-durata" className={`${secShow("sconti-durata") ? "" : "hidden"}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-semibold text-lg">Sconti durata</h3>
            <Button size="sm" variant="outline" onClick={() => setDurDiscs((d) => [...d, { months: 0, percent: 0 }])} className="gap-1.5" data-testid="duration-discount-add"><Plus className="h-4 w-4" /> Sconto</Button>
          </div>
          <p className="text-xs text-slate-400 mb-4">Applicati a tutte le stime (messaggio WhatsApp e Prezzo per m³). Es. 4 mesi → −10%.</p>
          <div className="space-y-2">
            {durDiscs.map((d, i) => (
              <div key={i} className="flex items-end gap-3 flex-wrap" data-testid={`duration-discount-row-${i}`}>
                <div>
                  <Label className="text-[10px]">Mesi</Label>
                  <Input type="number" value={d.months} onChange={(e) => setDurDiscs((ds) => ds.map((x, idx) => idx === i ? { ...x, months: e.target.value } : x))} className="h-9 w-28" data-testid={`duration-discount-months-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Sconto (%)</Label>
                  <Input type="number" value={d.percent} onChange={(e) => setDurDiscs((ds) => ds.map((x, idx) => idx === i ? { ...x, percent: e.target.value } : x))} className="h-9 w-28" data-testid={`duration-discount-percent-${i}`} />
                </div>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-red-400" onClick={() => setDurDiscs((ds) => ds.filter((_, idx) => idx !== i))} data-testid={`duration-discount-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {durDiscs.length === 0 && <p className="text-sm text-slate-400">Nessuno sconto durata configurato.</p>}
          </div>
        </section>

        <section id="sec-testi" className={`${secShow("testi") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-1">Testi messaggio WhatsApp</h3>
          <p className="text-xs text-slate-400 mb-4">Testi fissi usati nel messaggio. Usa *grassetto* e _corsivo_ dove serve. I prezzi e gli sconti vengono calcolati automaticamente.</p>
          <div className="space-y-3">
            <div><Label className="text-xs">Indirizzo deposito (origine "Apri in Maps")</Label><Input value={local.depotAddress ?? ""} onChange={(e) => setS("depotAddress", e.target.value)} className="mt-1" data-testid="setting-depotAddress" /></div>
            <div><Label className="text-xs">Saluto iniziale</Label><Input value={local.msgGreeting ?? ""} onChange={(e) => setS("msgGreeting", e.target.value)} className="mt-1" data-testid="setting-msgGreeting" /></div>
            <div><Label className="text-xs">Frase introduttiva</Label><Textarea rows={2} value={local.msgIntro ?? ""} onChange={(e) => setS("msgIntro", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgIntro" /></div>
            <div><Label className="text-xs">Frase introduttiva (modalità Area)</Label><Textarea rows={2} value={local.msgIntroArea ?? ""} onChange={(e) => setS("msgIntroArea", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgIntroArea" /></div>
            <div><Label className="text-xs">Disclaimer prezzo (modalità Area)</Label><Textarea rows={2} value={local.msgAreaDisclaimer ?? ""} onChange={(e) => setS("msgAreaDisclaimer", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgAreaDisclaimer" /></div>
            <div><Label className="text-xs">Nota durata (dopo gli sconti)</Label><Textarea rows={3} value={local.msgDurationNote ?? ""} onChange={(e) => setS("msgDurationNote", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgDurationNote" /></div>
            <div><Label className="text-xs">Alternativa consegna in autonomia</Label><Textarea rows={2} value={local.msgAlternativa ?? ""} onChange={(e) => setS("msgAlternativa", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgAlternativa" /></div>
            <div><Label className="text-xs">Scatola porta abiti — descrizione</Label><Textarea rows={3} value={local.msgAbitiDesc ?? ""} onChange={(e) => setS("msgAbitiDesc", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgAbitiDesc" /></div>
            <div><Label className="text-xs">Scatola porta abiti — chiusura</Label><Textarea rows={2} value={local.msgAbitiClose ?? ""} onChange={(e) => setS("msgAbitiClose", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgAbitiClose" /></div>
            <div><Label className="text-xs">Consegna scatole e materiali</Label><Textarea rows={3} value={local.msgConsegnaMateriali ?? ""} onChange={(e) => setS("msgConsegnaMateriali", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgConsegnaMateriali" /></div>
            <div><Label className="text-xs">Perché scegliere StorMi (una voce per riga)</Label><Textarea rows={6} value={local.msgWhy ?? ""} onChange={(e) => setS("msgWhy", e.target.value)} className="mt-1 text-sm" data-testid="setting-msgWhy" /></div>
          </div>
        </section>

        <section id="sec-profitometro" className={`${secShow("profitometro") ? "" : "hidden"}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-semibold text-lg">Profit-o-metro (periodi e soglie)</h3>
            <Button size="sm" variant="outline" onClick={() => setGaugePeriods((g) => [...g, { months: 0, redMax: 45, yellowMax: 58, max: 80 }])} className="gap-1.5" data-testid="gauge-period-add"><Plus className="h-4 w-4" /> Periodo</Button>
          </div>
          <p className="text-xs text-slate-400 mb-4">Periodi mostrati nel Profit-o-metro (es. 1, 4, 8, 12 mesi) con soglie €/m³·mese indipendenti. Rosso sotto la 1ª soglia, giallo fino alla 2ª, verde oltre. Max = fondo scala.</p>
          <div className="space-y-2">
            {gaugePeriods.map((p, i) => (
              <div key={i} className="flex items-end gap-3 flex-wrap" data-testid={`gauge-period-row-${i}`}>
                <div>
                  <Label className="text-[10px]">Mesi</Label>
                  <Input type="number" value={p.months} onChange={(e) => setGaugePeriods((gs) => gs.map((x, idx) => idx === i ? { ...x, months: e.target.value } : x))} className="h-9 w-20" data-testid={`gauge-period-months-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Rosso/giallo</Label>
                  <Input type="number" value={p.redMax} onChange={(e) => setGaugePeriods((gs) => gs.map((x, idx) => idx === i ? { ...x, redMax: e.target.value } : x))} className="h-9 w-24" data-testid={`gauge-period-red-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Giallo/verde</Label>
                  <Input type="number" value={p.yellowMax} onChange={(e) => setGaugePeriods((gs) => gs.map((x, idx) => idx === i ? { ...x, yellowMax: e.target.value } : x))} className="h-9 w-24" data-testid={`gauge-period-yellow-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Fondo scala</Label>
                  <Input type="number" value={p.max} onChange={(e) => setGaugePeriods((gs) => gs.map((x, idx) => idx === i ? { ...x, max: e.target.value } : x))} className="h-9 w-24" data-testid={`gauge-period-max-${i}`} />
                </div>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-red-400" onClick={() => setGaugePeriods((gs) => gs.filter((_, idx) => idx !== i))} data-testid={`gauge-period-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {gaugePeriods.length === 0 && <p className="text-sm text-slate-400">Nessun periodo configurato.</p>}
          </div>
        </section>

        <section id="sec-prompt-ai" className={`${secShow("prompt-ai") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-1">Prompt AI — Ottimizza con l'AI</h3>
          <p className="text-xs text-slate-400 mb-4">Istruzioni (system prompt) usate dall'AI quando premi "Polish with AI" sul messaggio WhatsApp. Puoi personalizzare tono e regole. Lascia le regole sui prezzi per evitare che l'AI cambi le cifre.</p>
          <Textarea rows={12} value={local.msgPolishPrompt ?? ""} onChange={(e) => setS("msgPolishPrompt", e.target.value)} className="text-sm font-mono" data-testid="setting-msgPolishPrompt" />

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-semibold text-lg">Messaggio WhatsApp con AI</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Attivo</span>
                <Switch
                  checked={local.textAiEnabled === true || local.textAiEnabled === "true"}
                  onCheckedChange={(v) => setS("textAiEnabled", v ? "true" : "false")}
                  data-testid="setting-textAiEnabled" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Se attivo, in <span className="font-medium">tutte le modalità</span> (Per oggetti, Per area, Da testo, Da immagine) potrai generare il messaggio WhatsApp con l'AI seguendo le istruzioni qui sotto (invece del template fisso). Il pulsante "Genera messaggio con AI" compare nel pannello del preventivo. Se disattivo, resta il template classico.
            </p>
            <Textarea rows={16} value={local.textAiPrompt ?? ""} onChange={(e) => setS("textAiPrompt", e.target.value)} className="text-sm font-mono" data-testid="setting-textAiPrompt" />
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <h3 className="font-display font-semibold text-lg mb-1">Chat semplificata — Istruzioni assistente</h3>
            <p className="text-xs text-slate-400 mb-4">
              System prompt usato dalla modalità <span className="font-medium">"Da testo (semplificato)"</span>: l'assistente segue queste istruzioni (listino, sconti, ritiro, struttura del messaggio) per generare il preventivo WhatsApp dalla chat. Modificalo per aggiornare prezzi o regole.
            </p>
            <Textarea rows={20} value={local.simpleChatPrompt ?? ""} onChange={(e) => setS("simpleChatPrompt", e.target.value)} className="text-sm font-mono" data-testid="setting-simpleChatPrompt" />
          </div>
        </section>

        <section id="sec-modelli-ai" className={`${secShow("modelli-ai") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-1">Modelli AI</h3>
          <p className="text-xs text-slate-400 mb-4">Scegli il modello OpenAI per ciascuna funzione AI. Accanto a ogni modello trovi una stima di <span className="font-medium">costo/velocità</span> (prezzo input/output per 1M token, indicativo — verifica il listino OpenAI). Per l'analisi foto usa un modello con capacità di visione.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            {AI_FUNCTIONS.map(([key, label]) => {
              const sel = local[key] ?? "gpt-4o";
              const meta = MODEL_META[sel] || {};
              return (
                <div key={key}>
                  <Label className="text-xs">{label}</Label>
                  <Select value={sel} onValueChange={(v) => setS(key, v)}>
                    <SelectTrigger className="mt-1" data-testid={`setting-${key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {AI_MODELS.map((m) => {
                        const mm = MODEL_META[m] || {};
                        return (
                          <SelectItem key={m} value={m} data-testid={`${key}-option-${m}`}>
                            {m} — {mm.tier} · ⚡{mm.speed} · 💰{mm.price}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] mt-1.5 flex items-center gap-1.5" data-testid={`model-hint-${key}`}>
                    <span className={`px-1.5 py-0.5 rounded-full ${TIER_COLOR[meta.tier] || "bg-slate-100 text-slate-600"}`}>{meta.tier}</span>
                    <span className="text-slate-400">⚡ {meta.speed} · 💰 {meta.price}</span>
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {secShow("statistiche") && <AiUsageStats isAdmin={isAdmin} />}

        <section id="sec-self-storage" className={`${secShow("self-storage") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-4">Confronto self-storage</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label className="text-xs">Canone mensile (es. "39-140")</Label><Input value={local.ssCanone ?? ""} onChange={(e) => setS("ssCanone", e.target.value)} className="mt-1" data-testid="setting-ssCanone" /></div>
            <div><Label className="text-xs">Cauzione iniziale</Label><Input value={local.ssCauzione ?? ""} onChange={(e) => setS("ssCauzione", e.target.value)} className="mt-1" data-testid="setting-ssCauzione" /></div>
            <div><Label className="text-xs">Trasporto</Label><Input value={local.ssTrasporto ?? ""} onChange={(e) => setS("ssTrasporto", e.target.value)} className="mt-1" data-testid="setting-ssTrasporto" /></div>
          </div>
        </section>

        <section id="sec-catalogo" className={`${secShow("catalogo") ? "" : "hidden"}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg">Gestione catalogo</h3>
            <Button size="sm" variant="outline" onClick={addRow} className="gap-1.5" data-testid="catalog-add-row"><Plus className="h-4 w-4" /> Aggiungi</Button>
          </div>

          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="listino-card">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-md bg-[#1a5f5a]/10 flex items-center justify-center shrink-0">
                <FileSpreadsheet className="h-5 w-5 text-[#1a5f5a]" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm text-slate-800 truncate">{fileInfo.fileName || "Nessun listino caricato"}</div>
                <div className="text-xs text-slate-400">
                  Ultimo aggiornamento: {fileInfo.updatedAt ? formatDateIt(fileInfo.updatedAt) : "—"}
                  {catalog.length ? ` · ${catalog.length} articoli` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => window.open(`${API}/catalog/download`, "_blank")} data-testid="listino-download">
                <Download className="h-4 w-4" /> Scarica
              </Button>
              <Button size="sm" className="bg-[#1a5f5a] hover:bg-[#134744] gap-1.5" onClick={() => xlsxRef.current?.click()} disabled={reuploading} data-testid="listino-reupload">
                {reuploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ricarica listino
              </Button>
              <input ref={xlsxRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => reupload(e.target.files[0])} data-testid="listino-file-input" />
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm min-w-[820px]">
              <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left p-2">Nome</th><th className="p-2">Categoria</th><th className="p-2">€/mese</th>
                <th className="p-2">L</th><th className="p-2">W</th><th className="p-2">H</th><th className="p-2">Facch.</th><th className="p-2">Imb.</th><th className="p-2" title="Considera nel Profit-o-metro">Profit</th><th className="p-2"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="p-1.5"><Input value={r.label} onChange={(e) => setRow(i, "label", e.target.value)} className="h-8 min-w-[140px]" /></td>
                    <td className="p-1.5">
                      <Input value={r.category} onChange={(e) => setRow(i, "category", e.target.value)} className="h-8 w-[150px]" />
                    </td>
                    {["price_eur", "length_cm", "width_cm", "height_cm", "fach_minutes", "imb_minutes"].map((k) => (
                      <td key={k} className="p-1.5"><Input type="number" value={r[k]} onChange={(e) => setRow(i, k, parseFloat(e.target.value) || 0)} className="h-8 w-[70px]" /></td>
                    ))}
                    <td className="p-1.5 text-center">
                      <Switch checked={r.count_in_profit !== false} onCheckedChange={(v) => setRow(i, "count_in_profit", v)} data-testid={`catalog-profit-${i}`} />
                    </td>
                    <td className="p-1.5"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => delRow(i)}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="sec-materiali-imballaggio" className={`${secShow("materiali-imballaggio") ? "" : "hidden"}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-lg">Materiali d'imballaggio</h3>
              <p className="text-xs text-slate-400">Catalogo separato usato nella sezione "Materiali d'imballaggio richiesti".</p>
            </div>
            <Button size="sm" variant="outline" onClick={addPackRow} className="gap-1.5" data-testid="packing-add-row"><Plus className="h-4 w-4" /> Aggiungi</Button>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left p-2">Nome</th><th className="p-2">Dimensioni (LxPxH)</th><th className="p-2">Dimensioni stoccate</th><th className="p-2">Costo €</th><th className="p-2">Prezzo cliente €</th><th className="p-2"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {packRows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="p-1.5"><Input value={r.label} onChange={(e) => setPackRow(i, "label", e.target.value)} className="h-8 min-w-[150px]" data-testid={`packing-row-label-${i}`} /></td>
                    <td className="p-1.5"><Input value={r.dimensions || ""} onChange={(e) => setPackRow(i, "dimensions", e.target.value)} className="h-8 w-[130px]" /></td>
                    <td className="p-1.5"><Input value={r.stocked || ""} onChange={(e) => setPackRow(i, "stocked", e.target.value)} className="h-8 w-[130px]" /></td>
                    <td className="p-1.5"><Input type="number" value={r.cost_eur} onChange={(e) => setPackRow(i, "cost_eur", parseFloat(e.target.value) || 0)} className="h-8 w-[90px]" data-testid={`packing-row-cost-${i}`} /></td>
                    <td className="p-1.5"><Input type="number" value={r.client_eur} onChange={(e) => setPackRow(i, "client_eur", parseFloat(e.target.value) || 0)} className="h-8 w-[90px]" data-testid={`packing-row-client-${i}`} /></td>
                    <td className="p-1.5"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => delPackRow(i)}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>


        <section id="sec-sconti-volume" className={`${secShow("sconti-volume") ? "" : "hidden"}`}>
          <h3 className="font-display font-semibold text-lg mb-4">Sconti volume</h3>
          <div className="flex items-center gap-3 mb-4">
            <Select value={discLabel} onValueChange={setDiscLabel}>
              <SelectTrigger className="w-[260px]" data-testid="discount-select"><SelectValue placeholder="Seleziona oggetto" /></SelectTrigger>
              <SelectContent>{catalog.map((c) => <SelectItem key={c.label} value={c.label}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setTiers((t) => [...t, { min_qty: 1, max_qty: "", price_per_unit: 0 }])} className="gap-1.5"><Plus className="h-4 w-4" /> Fascia</Button>
          </div>
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <div><Label className="text-[10px]">Qtà min</Label><Input type="number" value={t.min_qty} onChange={(e) => setTiers((ts) => ts.map((x, idx) => idx === i ? { ...x, min_qty: e.target.value } : x))} className="h-8 w-24" /></div>
                <div><Label className="text-[10px]">Qtà max</Label><Input type="number" value={t.max_qty} placeholder="∞" onChange={(e) => setTiers((ts) => ts.map((x, idx) => idx === i ? { ...x, max_qty: e.target.value } : x))} className="h-8 w-24" /></div>
                <div><Label className="text-[10px]">€/unità</Label><Input type="number" value={t.price_per_unit} onChange={(e) => setTiers((ts) => ts.map((x, idx) => idx === i ? { ...x, price_per_unit: e.target.value } : x))} className="h-8 w-28" /></div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 mt-4" onClick={() => setTiers((ts) => ts.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {tiers.length === 0 && <p className="text-sm text-slate-400">Nessuna fascia di sconto per questo oggetto.</p>}
          </div>
          <Button size="sm" onClick={saveDiscounts} disabled={discSaving} className="mt-4 bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="discount-save-button">
            {discSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva sconti
          </Button>
        </section>

        {isAdmin && activeSec === "gestione-utenti" && <UserManagement currentUserId={user?.id} />}
        {isAdmin && activeSec === "registro-attivita" && <ActivityLog />}
        </div>
      </div>
    </div>
  );
}
